import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {DURATION} from "@app/core/config/snack-bar.config";

import {BehaviorSubject, combineLatest, forkJoin, Observable, of} from 'rxjs';
import {catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, take, tap} from 'rxjs/operators';

import {STORAGE_KEY} from '@app/core/config/app.config';

import AlphaLevelFacade from '../facades/alpha-level.facade';
import VersionFacade from '../facades/version.facade';
import Guide from '../models/guide.model';
import GuideService from '../services/guide/guide.service';

@Injectable({providedIn: 'root'})
export default class GuideState {
  public guides$: Observable<Guide[]> = this._service.list().pipe(
    catchError(() => {
      this._snackBar.open('There was an issue when trying to get the list of guides.', 'X', {
        duration:         DURATION,
        panelClass:       ['alert-snackbar'],
        verticalPosition: 'bottom'
      });

      return of([]);
    }),
    shareReplay(1)
  );

  public guidesPerLevel$: Observable<Guide[]> = combineLatest([this.guides$, this._levelFacade.level$]).pipe(
    filter(([guides, level]) => Boolean(guides && level)),
    map(([guides, level]) => guides.filter(guide => guide.levels.includes(level)))
  );

  private readonly _selectedGuidePerLevelId = new BehaviorSubject<string>(localStorage.getItem(STORAGE_KEY.selectedGuidePerLevelId));

  public selectedGuidePerLevelId$ = combineLatest([this._selectedGuidePerLevelId, this.guidesPerLevel$]).pipe(
    switchMap(([id, guidesPerLevel]) => forkJoin({guidesPerLevel: of(guidesPerLevel), id: of(id)})),
    map(({guidesPerLevel, id}) => {
      if(!id && guidesPerLevel.length) {
        const [firstGuide] = guidesPerLevel;
        this.updateSelectedGuidePerLevelId(firstGuide.id);
        return firstGuide.id;
      }

      if(id && !guidesPerLevel.length) {
        const defaultId = '';
        this.updateSelectedGuidePerLevelId(defaultId);
        return defaultId;
      }

      return id;
    }),
    distinctUntilChanged(),
    tap(() => {
      this._versionFacade
      .checkIfVersionsAreDifferent()
      .pipe(take(1))
      .subscribe(different => this._versionFacade.processCheckIfVersionsAreDifferent(different));
    }),
    shareReplay(1)
  );

  public constructor(
    private readonly _service: GuideService,
    private readonly _levelFacade: AlphaLevelFacade,
    private readonly _snackBar: MatSnackBar,
    private readonly _versionFacade: VersionFacade
  ) {}

  public updateSelectedGuidePerLevelId(id: string): void {
    this._selectedGuidePerLevelId.next(id);
    localStorage.setItem(STORAGE_KEY.selectedGuidePerLevelId, id);
  }
}
